import React, { useEffect, useMemo, useRef, useState } from 'react';

const HOUR_PX = 44;
const FIRST_VISIBLE_HOUR = 7;
const VISIBLE_HOURS = 15; // 7am – 10pm
const PRIORITY_STYLE = {
  High: 'border-red-500 bg-red-50 text-red-900',
  Medium: 'border-amber-500 bg-amber-50 text-amber-900',
  Low: 'border-emerald-500 bg-emerald-50 text-emerald-900',
};

const pad = (n) => String(n).padStart(2, '0');
const dayId = (d) => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;

const toMinutes = (time) => {
  if (!time) return null;
  const [h, m] = String(time).split(':').map(Number);
  return Number.isFinite(h) ? h * 60 + (m || 0) : null;
};

// Cascade columns for tasks that start within the same hour
const layoutDay = (tasks) => {
  const timed = tasks
    .map((task) => ({ task, start: toMinutes(task.time) }))
    .filter((x) => x.start !== null)
    .sort((a, b) => a.start - b.start);
  const columnEnds = [];
  timed.forEach((item) => {
    let col = columnEnds.findIndex((end) => end <= item.start);
    if (col === -1) col = columnEnds.length;
    columnEnds[col] = item.start + 60;
    item.col = col;
  });
  // Shorten a block when the next task starts within its hour so its title stays visible
  timed.forEach((item, i) => {
    const next = timed.slice(i + 1).find((x) => x.start > item.start);
    const span = next && next.start - item.start < 60 ? Math.max(next.start - item.start, 25) : 60;
    item.height = (span / 60) * HOUR_PX - 2;
  });
  return timed;
};

export const WeekTimeGrid = ({ weekStart, weekDays, getTasksForDate, onTaskClick, onDayClick, selectedDate, onDropTask, allDayLabel }) => {
  const scrollRef = useRef(null);
  const canDrag = Boolean(onDropTask);
  // Slot under the dragged task: { key: dateString, hour: number | null (all-day row) }
  const [dragSlot, setDragSlot] = useState(null);
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i)),
    [weekStart]
  );
  const todayKey = new Date().toDateString();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = FIRST_VISIBLE_HOUR * HOUR_PX;
  }, [weekStart]);

  // Hour grid drop sets day + whole hour; All day row drop only changes the day
  const zoneHandlers = (date, withHour) => {
    if (!canDrag) return {};
    const hourAt = (e) =>
      Math.min(23, Math.max(0, Math.floor((e.clientY - e.currentTarget.getBoundingClientRect().top) / HOUR_PX)));
    return {
      onDragOver: (e) => {
        if (!e.dataTransfer.types.includes('text/task-id')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const hour = withHour ? hourAt(e) : null;
        if (dragSlot?.key !== date.toDateString() || dragSlot?.hour !== hour) setDragSlot({ key: date.toDateString(), hour });
      },
      onDragLeave: (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setDragSlot(null);
      },
      onDrop: (e) => {
        e.preventDefault();
        setDragSlot(null);
        const id = e.dataTransfer.getData('text/task-id');
        if (id) onDropTask(date, id, withHour ? `${pad(hourAt(e))}:00` : null);
      },
    };
  };

  // Plain render helper (not a component) so drag-over re-renders don't remount blocks mid-drag
  const renderBlock = ({ task, style, compact }) => (
    <button
      key={task.id}
      type="button"
      draggable={canDrag}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/task-id', task.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragEnd={() => setDragSlot(null)}
      onClick={(e) => { e.stopPropagation(); onTaskClick(task); }}
      data-testid={`week-task-${task.id}`}
      title={`${task.time ? `${task.time} · ` : ''}${task.title}`}
      style={style}
      className={`text-left rounded-md border-l-4 px-1.5 py-1 text-[11px] leading-tight shadow-sm overflow-hidden hover:shadow-md transition-shadow ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''} ${PRIORITY_STYLE[task.priority] || PRIORITY_STYLE.Medium} ${task.completed ? 'opacity-50 line-through' : ''}`}
    >
      {compact ? (
        <span className="block truncate"><span className="font-semibold">{task.time}</span> {task.title}</span>
      ) : (
        <>
          {task.time && <span className="block font-semibold">{task.time}</span>}
          <span className="block truncate">{task.title}</span>
        </>
      )}
    </button>
  );

  return (
    <div className="bg-white rounded-2xl p-3 shadow-sm mb-4" data-testid="week-time-grid">
      <div className="grid grid-cols-[3rem_repeat(7,minmax(0,1fr))] gap-px">
        <div />
        {days.map((d, i) => {
          const selected = selectedDate && d.toDateString() === selectedDate.toDateString();
          const openCount = getTasksForDate(d).filter((task) => !task.completed).length;
          return (
            <button
              key={d.toDateString()}
              onClick={() => onDayClick(d)}
              data-testid={`week-day-header-${dayId(d)}`}
              className={`py-1.5 rounded-lg text-center transition-colors ${selected ? 'bg-blue-100' : 'hover:bg-gray-50'}`}
            >
              <span className="block text-[11px] font-medium text-gray-500">{weekDays[i]}</span>
              <span className={`inline-flex w-7 h-7 items-center justify-center rounded-full text-sm font-semibold ${d.toDateString() === todayKey ? 'bg-blue-600 text-white' : 'text-gray-900'}`}>{d.getDate()}</span>
              <span
                data-testid={`week-day-count-${dayId(d)}`}
                className={`mt-0.5 block text-[10px] font-semibold leading-4 ${openCount === 0 ? 'text-gray-300' : openCount >= 4 ? 'text-amber-700' : 'text-gray-500'}`}
              >
                {openCount === 0 ? '–' : `${openCount} open`}
              </span>
            </button>
          );
        })}
        <div className="text-[10px] text-gray-400 pt-1.5 pr-1 text-right">{allDayLabel}</div>
        {days.map((d) => (
          <div
            key={`allday-${d.toDateString()}`}
            {...zoneHandlers(d, false)}
            className={`min-h-[2rem] p-0.5 space-y-0.5 rounded-md ${dragSlot?.key === d.toDateString() && dragSlot.hour === null ? 'bg-indigo-100 ring-2 ring-indigo-400' : 'bg-gray-50'}`}
            data-testid={`week-allday-${dayId(d)}`}
          >
            {getTasksForDate(d).filter((task) => !task.time || toMinutes(task.time) === null).map((task) => (
              renderBlock({ task, style: { width: '100%' } })
            ))}
          </div>
        ))}
      </div>

      <div ref={scrollRef} className="mt-2 overflow-y-auto" style={{ maxHeight: VISIBLE_HOURS * HOUR_PX }} data-testid="week-time-scroll">
        <div className="grid grid-cols-[3rem_repeat(7,minmax(0,1fr))] relative" style={{ height: 24 * HOUR_PX }}>
          <div className="relative">
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} className="absolute right-1 text-[10px] text-gray-400 leading-none" style={{ top: h * HOUR_PX + 2 }}>
                {h === 0 ? '' : `${pad(h)}:00`}
              </div>
            ))}
          </div>
          {days.map((d) => (
            <div
              key={`col-${d.toDateString()}`}
              {...zoneHandlers(d, true)}
              onClick={() => onDayClick(d)}
              className={`relative border-l border-gray-100 ${d.toDateString() === todayKey ? 'bg-blue-50/40' : ''}`}
              data-testid={`week-col-${dayId(d)}`}
            >
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} className="absolute inset-x-0 border-t border-gray-100" style={{ top: h * HOUR_PX }} />
              ))}
              {dragSlot?.key === d.toDateString() && dragSlot.hour !== null && (
                <div
                  className="absolute inset-x-0.5 rounded-md bg-indigo-100/80 ring-2 ring-indigo-400 text-[10px] font-semibold text-indigo-700 px-1 pointer-events-none"
                  style={{ top: dragSlot.hour * HOUR_PX + 1, height: HOUR_PX - 2, zIndex: 20 }}
                  data-testid="week-drop-slot"
                >
                  {pad(dragSlot.hour)}:00
                </div>
              )}
              {layoutDay(getTasksForDate(d)).map(({ task, start, col, height }) => (
                renderBlock({
                  task,
                  compact: height < 36,
                  style: {
                    position: 'absolute',
                    top: (start / 60) * HOUR_PX + 1,
                    height,
                    left: 2 + col * 10,
                    right: 2,
                    zIndex: 1 + col,
                  },
                })
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeekTimeGrid;
